<script setup lang="ts">
import useNetwork, { networkConfig } from '@/composables/useNetwork';
import useNumbers from '@/composables/useNumbers';
import { PoolNote, Vault } from '@/types/untangled/pool';
import RightIcon from './RightIcon.vue';
import { calculateApy } from '@/lib/utils/util.service';
import { fxCommonUtil } from '@/lib/utils/fxCommon';
import { configService } from '@/services/config/config.service';
import useVaultPoolData from '@/composables/queries/useVaultPoolData';
import { formatUnits } from '@ethersproject/units';
import { useGetVaultAPY } from '@/services/api/pool.service';
import { useRouter } from 'vue-router';

/**
 * TYPES
 */
type Props = {
  poolNote: Vault;
  poolValue: number;
};
const props = defineProps<Props>();

/**
 * COMPOSABLES
 */
const poolCard = ref<any>();
const targetReturnNotification = ref();
const loading = ref(false);
console.log('PoolCard.vue - poolNote');
console.log(props.poolNote);
const poolNote = toRef(props, 'poolNote');
const vaultContractAddress = computed(() => poolNote.value?.vaultAddress);
const { vaultPoolData, loading: isVaultPoolDataLoading } = useVaultPoolData({
  vaultAddress: vaultContractAddress,
});
console.log('PoolCard.vue - vaultPoolData');
console.log(vaultPoolData);

const { fNum } = useNumbers();
const { networkSlug } = useNetwork();
const router = useRouter();

/**
 * COMPUTED
 */
const isHasSOT = ref(false);
const isHasJOT = ref(false);
const apy = ref();

// const currencySymbol = computed(() =>
//   fxCommonUtil.getCurrencySymbol(props.poolNote.currency?.shortCode || '')
// );

watchEffect(async () => {
  if (!apy.value) {
    useGetVaultAPY().then(({ data }) => {
      apy.value = data.value;
    });
  }
});

const tags = computed(() => {
  const assetType = '';
  const country = '';

  return [country, assetType].filter(Boolean);
});

const targetReturn = computed(() => {
  if (apy.value && apy.value.apy > 0) {
    return `${fNum(apy.value.apy, {
      minimumFractionDigits: 2,
    })}%`;
  }
  if (targetReturnNotification.value) {
    return targetReturnNotification.value;
  }

  return isHasSOT.value
    ? `${fNum(calculateApy(props.poolNote), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} %`
    : 'Variable';
});

const keyValues = computed<
  {
    key: string;
    value: string;
    tooltip?: string;
  }[]
>(() => {
  console.log('PoolCard.vue - poolNote.value');
  console.log(poolNote.value);
  if (poolNote.value.chainId == 100) {
    const values = [
      {
        key: 'TVL',
        value:
          poolNote.value?.underlyingAssetSymbol +
          ' ' +
          fxCommonUtil.numberFormat(
            formatUnits(
              vaultPoolData.value.totalAssets,
              networkConfig.vaultToken.assetDecimals
            )
          ),
      },
      {
        key: 'Capacity %',
        value: '0',
      },
      {
        key: 'Liquidity',
        value: '3 days',
        tooltip: 'Withdrawal requests are fulfilled within 24 hours',
      },
    ];
    return values;
  }
  const values = [
    {
      key: 'TVL',
      value:
        poolNote.value?.underlyingAssetSymbol +
        ' ' +
        fxCommonUtil.numberFormat(
          formatUnits(
            vaultPoolData.value.totalAssets,
            networkConfig.vaultToken.assetDecimals
          )
        ),
    },
    {
      key: 'Reward',
      value: '100m Tangle Points',
    },
    {
      key: 'Liquidity',
      value: '24 hours',
      tooltip: 'Withdrawal requests are fulfilled within 24 hours',
    },
  ];
  return values;
});

function redirectToExternal() {
  if (
    props.poolNote.vaultAddress ==
    '9kwoKmpNtPEK4fESLf7YnrGtZqvHcnYoN7aVWWdvkhSP'
  ) {
    window.location.href =
      'https://usdn.untangled.finance/vaults/9kwoKmpNtPEK4fESLf7YnrGtZqvHcnYoN7aVWWdvkhSP';
    return;
  }
  router.push({
    name: 'vault',
    params: {
      id: props.poolNote.vaultAddress,
      networkSlug,
    },
  });
}

const poolChainName = computed(() => {
  const chainId = props?.poolNote?.chainId;
  const chainName = configService.getNetworkConfig(chainId)?.chainName;
  return chainName;
});
</script>

<template>
  <div
    :class="[
      'poolCard relative',
      'w-full rounded-[12px] shadow-lg transition-all duration-200',
      'flex flex-col gap-y-12',
      'pt-8 pb-4',
      'border dark:border-gray-850',
      'bg-white dark:bg-gray-850 hover:bg-gray-50 dark:hover:bg-gray-800',
      'transition-colors ease-in duration-300',
    ]"
  >
    <div
      v-if="poolChainName"
      class="absolute top-4 right-4 justify-center py-1 px-4 w-auto text-xs text-gray-600 border border-gray-600 rounded-[36px]"
    >
      {{ poolChainName }}
    </div>
    <div class="flex flex-col gap-2 px-4">
      <div class="mb-2 h-4 filter poolOriginatorLogo">
        <!--
        <img
          class="w-auto h-full"
          :src="props.poolNote.pool.poolProfilePhoto"
        />
-->
      </div>

      <span class="text-xl font-bold">{{ props.poolNote?.details?.name }}</span>

      <div class="flex gap-6 items-center text-sm">
        <span
          class="overflow-hidden flex-1 text-gray-500 dark:text-gray-400 truncate text-ellipsis"
          >{{ props.poolNote?.details?.description }}</span
        >
        <div class="flex gap-2 items-center">
          <div
            v-for="(tag, index) in tags"
            :key="index"
            :class="[
              'px-3 py-1',
              'flex justify-center items-center',
              'bg-gray-200',
              'text-neutral-400 dark:text-white dark:bg-gray-700 whitespace-nowrap',
              'rounded-[36px]',
            ]"
          >
            {{ tag }}
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-between items-center px-4">
      <div class="flex flex-col">
        <span class="text-md">APY</span>
        <BalLoadingBlock v-if="loading" class="mt-4 w-20 h-8" />
        <span v-else class="text-3xl font-bold" v-html="targetReturn" />
      </div>

      <BalBtn
        class="poolBtn"
        color="transparent"
        circle
        flat
        outline
        tag="button"
        @click="redirectToExternal"
      >
        <RightIcon />
      </BalBtn>
    </div>

    <div class="flex flex-col gap-y-2 px-4">
      <div
        v-for="{ key, value, tooltip } in keyValues"
        :key="key"
        :class="[
          'flex justify-between items-center gap-2',
          'px-4 py-2',
          'border border-gray-200 dark:border-gray-700 rounded-md',
        ]"
      >
        <div class="flex gap-2 items-center">
          <span>{{ key }}</span>
          <BalTooltip v-if="tooltip" width="48" iconSize="sm" class="mt-1">
            <div v-html="tooltip"></div>
          </BalTooltip>
        </div>
        <span class="font-bold text-right">{{ value }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.poolBtn {
  opacity: 0;
  transition: all 150ms ease-in-out;
  flex-shrink: 0;

  @apply border-gray-200 hover:border-green-500 dark:border-gray-700 dark:hover:border-green-500;
}

.poolCard:hover .poolBtn {
  opacity: 100;
}

.poolOriginatorLogo {
  @apply grayscale transition-all;
}

.poolCard:hover .poolOriginatorLogo {
  @apply grayscale-0;
}
</style>
